import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { OrderQuery } from '@modules/order/state/order-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { SendToOption } from '@shared/models/send-to-option';
import { SendToIdForNotLabsEnum } from '@modules/order/models/send-to-type.enum';
import { CaseTypeEnum } from '@modules/order/models/case-type.enum';
import { RxTemplate } from './rx-template.model';
import { DEFAULT_TEMPLATE, TEMPLATE_REGISTRY } from './rx-template.registry';

@Injectable({ providedIn: 'root' })
export class RxTemplateService {
	readonly template$: Observable<Readonly<RxTemplate>> = this.shellQuery.isProcedureFlow$.pipe(
		switchMap(isProcedureFlow =>
			isProcedureFlow
				? this.orderQuery.sendTo$.pipe(map(sendTo => this.resolveTemplate(sendTo)))
				: this.orderQuery.caseType$.pipe(
						map(caseType => this.resolveCaseTypeFlowTemplate(caseType?.Id ?? this.shellQuery.rx?.Order?.CaseTypeId))
					)
		),
		startWith(DEFAULT_TEMPLATE),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	get currentTemplate(): Readonly<RxTemplate> {
		return this.shellQuery.isProcedureFlow
			? this.resolveTemplate(this.orderQuery.sendTo)
			: this.resolveCaseTypeFlowTemplate(this.orderQuery.caseType?.Id ?? this.shellQuery.rx?.Order?.CaseTypeId);
	}

	constructor(
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery
	) {}

	resolveTemplate(sendTo: SendToOption | null): Readonly<RxTemplate> {
		if (sendTo && 'Settings' in sendTo) {
			const rxTemplateName = sendTo.Settings?.customRxIntegration.rxTemplate;

			if (rxTemplateName && TEMPLATE_REGISTRY[rxTemplateName]) {
				return TEMPLATE_REGISTRY[rxTemplateName];
			}
		}

		if (sendTo?.Id === SendToIdForNotLabsEnum.glidewell) {
			return TEMPLATE_REGISTRY.glidewell;
		}

		return DEFAULT_TEMPLATE;
	}

	private resolveCaseTypeFlowTemplate(caseTypeId: number | undefined): Readonly<RxTemplate> {
		if (caseTypeId === CaseTypeEnum.ChairSideMillingGlidewellIo) {
			return TEMPLATE_REGISTRY.glidewell;
		}

		return DEFAULT_TEMPLATE;
	}
}
